import { map } from "ramda";
import React from "react";
import { FaLock, FaSearch, FaTimesCircle, FaUsers } from "react-icons/fa";
import ToolTip from "react-tooltip";
import styled from "styled-components";
import { LIMIT_COURSES } from "../../actions/dashboard/dashboard.actions";
import {
  AddButton,
  CustomSearchInputContainer,
  FieldSet as CustomFieldSet,
} from "../../App/Common/Components/FormComponents";
import { IClass, ICourse, ICourseInstance, IScormPackage, RequestState } from "../../App/types/common";
import { LocaleNames } from "../../utils/locales";
import { Divider, Input } from "../pages/CourseAuthor/FormElements";
import { Header as H1 } from "../utils/Headings";
import * as Helper from "../utils/helpers";
import LanguageIcon from "../utils/Icons/LanguageIcon";
import { OpenPackageIcon, ClosedPackageIcon } from "../utils/Icons/PackageIcon";
import Spinner from "../utils/Spinner";
import { AnalyticsRedirect } from "./analytics/AnalyticsRedirect.component";
import { ClickableIconContainer } from "./analytics/AnalyticsRedirect.styles";
import { CourseAccessButtons } from "./CourseAccessButtons";
import {
  ClassItem,
  ClassList,
  ContentList,
  CourseItem,
  CourseList,
  DashboardIcon,
  ExternalLink,
  LanguageDashboardIcon,
  LinksContainer,
  LocaleContainer,
  LocaleItem,
  SCORMPackageDashboardIcon,
  ScormPackageItem,
  ScormPackageList,
  ScormPackageTag,
  ScormPackageTagContainer,
  Subtle,
  TextSpan,
} from "./DashboardPage.styles";

const Title = styled.h3`
  font-size: 2.2rem;
`;

const LockIcon = styled(FaLock)`
  margin-left: 0.6rem;
  padding-top: 3px;
`;
const LoadButton = styled(AddButton)`
  margin: 1rem;
  text-transform: uppercase;
`;

const LoadButtonContainer = styled.div`
  display: flex;
`;

const DisplayMessage = styled.p`
  text-align: center;
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const SearchInputContainer = styled(CustomSearchInputContainer)`
  background-color: ${({ theme }) => theme.colors.lightgrey3};
  margin-right: 1rem;
  padding: unset;
`;

const SearchInput = styled(Input)`
  border: none;
  background: none;
  font-size: ${({ theme }) => theme.fontSizes.small};
  margin: 0 1rem;
  padding: 0.5rem;

  ::placeholder {
    color: ${({ theme }) => theme.colors.lightgrey4};
    font-style: italic;
  }
`;

const SearchButton = styled(AddButton)`
  padding: 0.5rem 0.1rem;
  width: 6rem;
`;

const FieldSet = styled(CustomFieldSet)`
  margin: 0 1rem;
  width: 50%;
`;

const displayMessage = (courses: ICourse[], allCourseIds: string[]) => {
  const shownCourses = courses.filter(course => allCourseIds.includes(course.courseId));
  return `Displaying ${shownCourses.length}/${allCourseIds.length} courses.`;
};

export interface IDashboardProps {
  accountId: string;
  allCourseIdsOnDashboard: string[];
  editableCourses: string[];
  courseInstances: ICourseInstance[];
  courses: ICourse[];
  classes: IClass[];
  failedCoursesIds: string[];
  loadAllCourses: () => void;
  loadMoreCourses: () => void;
  loadingMoreCoursesState: RequestState;
  scormPackages: IScormPackage[];
  searchCourses: (searchInput: string) => void;
  userIsAuthor: (courseId: string) => boolean;
}

export class DashboardPage extends React.Component<IDashboardProps> {
  public static classesByCourse: (
    classes: IClass[]
  ) => {
    [k: string]: IClass[];
  } = classes => {
    const withDupes = classes.reduce((acc: { [k: string]: IClass[] }, c: IClass) => {
      c.courseIds.forEach((cid: string) => {
        if (acc[cid] === undefined) {
          acc[cid] = [c];
        } else {
          acc[cid] = [...acc[cid], c];
        }
      });
      return acc;
    }, {});
    return map((clss: IClass[]) => Array.from(new Set(clss)), withDupes);
  };

  public buttonType = ["Test", "Preview/Print", "Light Edit", "Edit"];
  constructor(props: IDashboardProps) {
    super(props);
    this.state = {
      showTooltip: false,
    };
  }

  public render() {
    const {
      allCourseIdsOnDashboard,
      accountId,
      editableCourses,
      courses,
      classes,
      failedCoursesIds,
      loadAllCourses,
      loadMoreCourses,
      loadingMoreCoursesState,
      scormPackages,
      searchCourses,
    } = this.props;
    const inputRef: React.RefObject<HTMLInputElement> = React.createRef();
    const classesByCourse = DashboardPage.classesByCourse(classes);
    const scormPackagesByCourse = Helper.scormPackagesByCourse(scormPackages);
    const isLoadingMoreCourses = loadingMoreCoursesState === RequestState.PENDING;
    const hasMoreThanLimit = editableCourses.length >= LIMIT_COURSES;
    const searchByGivenText = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      inputRef.current?.value && searchCourses(inputRef.current.value);
    };
    const restartSearch = () => searchCourses("");
    const canLoadMoreCourses = editableCourses.length > courses.length + failedCoursesIds.length;
    const displayLoadingButtons = canLoadMoreCourses && hasMoreThanLimit;

    return (
      <>
        <H1>My Dashboard</H1>
        <SearchForm onSubmit={searchByGivenText}>
          {isLoadingMoreCourses ? (
            <p>Loading...</p>
          ) : (
            <>
              <FieldSet>
                <legend>Course Search</legend>
                <SearchInputContainer>
                  <SearchInput ref={inputRef} type="text" placeholder="Start typing here" />
                  {allCourseIdsOnDashboard.length !== editableCourses.length && (
                    <>
                      <FaTimesCircle onClick={restartSearch} data-for="reset-search" data-tip="Reset search" />
                      <ToolTip id="reset-search" place="bottom" />
                    </>
                  )}
                </SearchInputContainer>
              </FieldSet>
              <SearchButton type="submit" data-tip="Search" data-for="search-icon">
                <FaSearch aria-label="search" />
                <ToolTip id="search-icon" place="top" />
              </SearchButton>
            </>
          )}
        </SearchForm>
        <CourseList>
          {courses
            .sort((c1, c2) => (c1.displayName < c2.displayName ? -1 : 1))
            .map(c => (
              <CourseItem key={c.courseId}>
                <table>
                  <tbody>
                    <tr>
                      <td className="title">
                        <Title>{c.displayName}</Title>
                        <Subtle>
                          <LanguageDashboardIcon>
                            <LanguageIcon />
                          </LanguageDashboardIcon>
                          <LocaleContainer>
                            {c.supportedLocales.map(sl => (
                              <LocaleItem key={sl} isMaster={sl === c.masterLocale}>
                                {LocaleNames[sl]}
                              </LocaleItem>
                            ))}
                          </LocaleContainer>
                        </Subtle>
                      </td>
                      <td style={{ verticalAlign: "text-top" }} className="actions">
                        <CourseAccessButtons
                          locked={c.locked}
                          courseId={c.courseId}
                          allowEdit={this.props.userIsAuthor(c.courseId)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <ClassList>
                  {classesByCourse[c.courseId] && (
                    <DashboardIcon>
                      <FaUsers />
                    </DashboardIcon>
                  )}
                  <ContentList>
                    {(classesByCourse[c.courseId] || []).map((cls: IClass) => (
                      <ClassItem key={cls.classId}>
                        <TextSpan>{cls.displayName}</TextSpan>
                        <AnalyticsRedirect isClass={true} courseId={c.courseId} objectId={cls.classId} />
                        <ClickableIconContainer className="icon-letter-mail disabled" />
                      </ClassItem>
                    ))}
                  </ContentList>
                </ClassList>
                {(scormPackagesByCourse[c.courseId] || []).map(sp => (
                  <ScormPackageList>
                    <SCORMPackageDashboardIcon key={sp.scormPackageId}>
                      {sp.state === "closed" ? <ClosedPackageIcon /> : <OpenPackageIcon />}
                    </SCORMPackageDashboardIcon>
                    <ContentList>
                      <ScormPackageItem key={sp.scormPackageId}>
                        <Subtle>
                          <TextSpan>{sp.name}</TextSpan>
                          {sp.state === "closed" && (
                            <LockIcon data-for="scormClosed" data-tip="true" aria-label="lockIcon" />
                          )}
                        </Subtle>
                        <ScormPackageTagContainer>
                          <ScormPackageTag>{this.showLmsName(sp.lms)}</ScormPackageTag>
                          <ScormPackageTag>SCORM {sp.scormVersion.replace("_", ".")}</ScormPackageTag>
                        </ScormPackageTagContainer>
                        <LinksContainer>
                          <AnalyticsRedirect isClass={false} courseId={c.courseId} objectId={sp.scormPackageId} />
                          <ExternalLink
                            href={`${document.body.getAttribute("data-saltcompliance-api-root")}/scormPackages/${
                              sp.scormPackageId
                            }/_scormExport?activeUser=${accountId}`}
                          >
                            <ClickableIconContainer className="icon-download" />
                          </ExternalLink>
                        </LinksContainer>
                      </ScormPackageItem>
                    </ContentList>
                  </ScormPackageList>
                ))}
                <Divider />
              </CourseItem>
            ))}
        </CourseList>

        {isLoadingMoreCourses && <Spinner data-testid="spinner" />}
        {hasMoreThanLimit && <DisplayMessage>{displayMessage(courses, editableCourses)}</DisplayMessage>}
        <DisplayMessage style={{ color: "tomato" }}>
          {failedCoursesIds.length > 0 && `We could not load ${failedCoursesIds.length} course(s).`}
        </DisplayMessage>
        {displayLoadingButtons && (
          <LoadButtonContainer>
            <LoadButton onClick={loadMoreCourses} disabled={isLoadingMoreCourses}>
              Load +{LIMIT_COURSES} More
            </LoadButton>
            <LoadButton onClick={loadAllCourses} disabled={isLoadingMoreCourses}>
              Load All
            </LoadButton>
          </LoadButtonContainer>
        )}
        {this.buttonType.map((key, idx) => (
          <ToolTip id={key} place="bottom" key={idx}>
            <i>{key}</i>
          </ToolTip>
        ))}
        <ToolTip id="scormClosed" place="bottom">
          <i>This Scorm Package is closed</i>
        </ToolTip>
        <ToolTip id="disabledEdit" place="bottom">
          <p>
            You cannot edit this course <br /> because it is locked or you <br /> don't have permissions
          </p>
        </ToolTip>
      </>
    );
  }

  // this is a mean to put spaces between words like `SumTotalAgain ==> Sum Total Again`
  private showLmsName(lms: string): string {
    return lms.replace(/([A-Z]+)/g, " $1");
  }
}
